/**
 * @file This file contains the SaveManager class, responsible for handling
 * saving and loading game state to/from localStorage, using a slot-based system.
 * @module SaveManager
 */

import { 
    GAME_TYPE_QUICK_CLICK, 
    GAME_TYPE_CAMPAIGN_CLICK, 
    GAME_TYPE_CAMPAIGN_ROLLING, 
    GAME_TYPE_AUTOSAVE_QUICK,
    GAME_TYPE_AUTOSAVE_CAMPAIGN,
    GAME_TYPE_AUTOSAVE_ROLLING 
} from '../constants/gameTypes.js';

export class SaveManager {
    /**
     * The key used to store save slots in localStorage.
     * @type {string}
     */
    static SAVE_SLOTS_KEY = 'mergeVillageSaveSlots';
    static PREFERENCES_KEY = 'mergeVillagePreferences';

    /**
     * Creates an instance of SaveManager.
     * Ensures the save slots array exists in localStorage upon instantiation.
     */
    constructor() {
        // Ensure the save slots array exists in localStorage.
        if (!localStorage.getItem(SaveManager.SAVE_SLOTS_KEY)) {
            localStorage.setItem(SaveManager.SAVE_SLOTS_KEY, JSON.stringify([]));
        }
    }

    /**
     * Retrieves all save slots from localStorage.
     * @returns {Array<object>} An array of save slot objects.
     */
    getAllSaveSlots() {
        return JSON.parse(localStorage.getItem(SaveManager.SAVE_SLOTS_KEY)) || [];
    }

    /**
     * Gets a specific save slot by its ID.
     * @param {string} slotId - The unique identifier for the save slot.
     * @returns {object|null} The save slot object or null if not found.
     */
    getSaveSlotById(slotId) {
        return this._findSlot(slot => slot.id === slotId);
    }

    /**
     * Gets a specific save slot by its type.
     * @param {string} type - The type of save ('Quick' or 'Campaign').
     * @returns {object|null} The save slot object or null if not found.
     */
    getSaveSlotByType(type, level = null) {
        return this._findSlot(slot => {
            if (type === GAME_TYPE_QUICK_CLICK || 
                type === GAME_TYPE_CAMPAIGN_CLICK ||
                type === GAME_TYPE_AUTOSAVE_QUICK ||
                type === GAME_TYPE_AUTOSAVE_CAMPAIGN ||
                type === GAME_TYPE_AUTOSAVE_ROLLING
            ) {
                return slot.type === type;
            } else {
                return slot.type === type && slot.level === level;
            }
        });
    }

    /**
     * Finds a save slot that matches a given predicate.
     * @param {function(object): boolean} predicate - The function to test each slot.
     * @returns {object|null} The found slot or null.
     * @private
     */
    _findSlot(predicate) {
        const slots = this.getAllSaveSlots();
        return slots.find(predicate) || null;
    }

    /**
     * Saves a game state to a new or existing slot.
     * If a slot with the same type and level (for campaign) exists, it's overwritten.
     * Otherwise, a new slot is created.
     * @param {object} gameState - The state of the game to save.
     * @param {string} type - The type of save ('Quick' or 'Campaign').
     * @param {number} [level] - The campaign level number, if applicable.
     */
    saveGameByType(gameState, type, level = null) {
        const slots = this.getAllSaveSlots();
        const now = new Date();

        const existingSlotIndex = slots.findIndex(slot => {
            if (type === GAME_TYPE_QUICK_CLICK || 
                type === GAME_TYPE_CAMPAIGN_CLICK ||
                type === GAME_TYPE_AUTOSAVE_QUICK ||
                type === GAME_TYPE_AUTOSAVE_CAMPAIGN ||
                type === GAME_TYPE_AUTOSAVE_ROLLING
            ) {
                return slot.type === type;
            } else {
                return slot.type === type && slot.level === level;
            }
        });
        let savedSlot;

        if (existingSlotIndex > -1) {
            // Overwrite existing slot.
            savedSlot = {
                ...slots[existingSlotIndex],
                gameState,
                updatedAt: now,
            };
            slots[existingSlotIndex] = savedSlot;
        } else {
            // Create a new slot.
            savedSlot = {
                id: `slot-${now.getTime()}-${Math.random()}`,
                type,
                level,
                createdAt: now,
                updatedAt: now,
                gameState,
            };
            slots.push(savedSlot);
        
        }

        localStorage.setItem(SaveManager.SAVE_SLOTS_KEY, JSON.stringify(slots));
        return savedSlot.id;
    }

    /**
     * Deletes a save slot by its ID.
     * @param {string} slotId - The unique identifier for the save slot.
     */
    deleteSaveSlot(slotId) {
        let slots = this.getAllSaveSlots();
        slots = slots.filter(slot => slot.id !== slotId);
        localStorage.setItem(SaveManager.SAVE_SLOTS_KEY, JSON.stringify(slots));
    }

    /**
     * Deletes all save slots.
     */
    deleteAllSaveSlots() {
        localStorage.setItem(SaveManager.SAVE_SLOTS_KEY, JSON.stringify([]));
    }

    /**
     * Saves user preferences to localStorage.
     * @param {object} preferences - The preferences object to save.
     */
    savePreferences(preferences) {
        localStorage.setItem(SaveManager.PREFERENCES_KEY, JSON.stringify(preferences));
    }

    /**
     * Loads user preferences from localStorage.
     * @returns {object} The loaded preferences object, or a default empty object if none found.
     */
    loadPreferences() {
        const preferences = localStorage.getItem(SaveManager.PREFERENCES_KEY);
        return preferences ? JSON.parse(preferences) : {};
    }
}
